import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  Typography,
  Stack,
  Chip,
  Paper,
  Button,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  CircularProgress,
  Fade,
  Snackbar,
  Alert,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Share as ShareIcon,
  TrendingUp as TrendingUpIcon,
  ArrowBack,
  ArrowForward,
  RestartAlt as RestartAltIcon,
  AccessTime as AccessTimeIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import Meta from '../components/Meta';
import BankDiscussion from '../components/BankDiscussion';
import BankInsightsTile from '../components/BankInsightsTile';
import StartBonusModal from '../components/StartBonusModal';
import CompleteBonusModal from '../components/CompleteBonusModal';
import ResetBonusModal from './ResetBonusModal';
import { useAuth } from '../util/auth';
import { resetBonusTracking, completeBankBonus, formatFirestoreTimestamp } from '../util/firebase';
import { useSnackbar } from 'notistack';
import { doc, getDoc, deleteDoc, updateDoc, setDoc, increment, writeBatch } from 'firebase/firestore';
import { db } from '../config/firebase';

const WebBankDetails = ({
  bank,
  isLoading,
  isAdmin,
  existingBonus,
  setExistingBonus,
  totalUsers,
  successRate,
  handleVote,
  handleStatusChange,
  handleBookmarkToggle,
  isBookmarked,
  startBonusModalOpen,
  setStartBonusModalOpen,
  completeBonusModalOpen,
  setCompleteBonusModalOpen,
  snackbar,
  setSnackbar,
  theme,
  onPreviousBank,
  onNextBank,
  hasPrevious,
  hasNext,
}) => {
  const navigate = useNavigate();
  const darkMode = theme.palette.mode === 'dark';
  const [resetBonusModalOpen, setResetBonusModalOpen] = useState(false);
  const [localExistingBonus, setLocalExistingBonus] = useState(existingBonus);
  const [localSuccessRate, setLocalSuccessRate] = useState(successRate);
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  
  // Modern color palette using theme colors
  const bgColors = {
    main: theme.palette.background.default,
    card: theme.palette.background.paper,
    paper: darkMode ? '#1A1F35' : alpha('#FAF8F5', 0.8),
    cardBg: theme.palette.background.paper,
    surface: darkMode ? '#1A1F35' : '#FAF8F5',
    surfaceLight: darkMode ? alpha('#1A1F35', 0.8) : alpha('#FAF8F5', 0.6),
    header: darkMode
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.dark, 0.3)} 100%)`
      : `linear-gradient(135deg, ${alpha('#FAF8F5', 0.95)} 0%, ${alpha('#F5F3EF', 0.9)} 100%)`,
    accent: theme.palette.secondary.main,
    primary: theme.palette.primary.main,
    primaryDark: theme.palette.primary.dark,
    primaryLight: theme.palette.primary.light,
    border: darkMode ? alpha('#374151', 0.8) : alpha('#8B7355', 0.12),
    text: {
      primary: theme.palette.text.primary,
      secondary: theme.palette.text.secondary
    }
  };

  const getStatusColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy':
        return {
          color: darkMode ? '#34D399' : '#059669',
          bgColor: darkMode ? alpha('#34D399', 0.12) : alpha('#059669', 0.08)
        };
      case 'hard':
        return {
          color: darkMode ? '#F87171' : '#DC2626',
          bgColor: darkMode ? alpha('#F87171', 0.12) : alpha('#DC2626', 0.08)
        };
      default:
        return {
          color: darkMode ? '#FBBF24' : '#D97706',
          bgColor: darkMode ? alpha('#FBBF24', 0.12) : alpha('#D97706', 0.08)
        };
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const fetchBankData = async () => {
    try {
      if (!bank?.id) return;
      
      const bankRef = doc(db, 'banks', bank.id);
      const bankDoc = await getDoc(bankRef);
      
      if (bankDoc.exists()) {
        const bankData = bankDoc.data();
        // Set success rate to 0 if no attempts
        const newSuccessRate = bankData.totalAttempts > 0 
          ? Math.min(100, Math.round((bankData.successfulCompletions / bankData.totalAttempts) * 100))
          : 0;
        setLocalSuccessRate(newSuccessRate);
      }
    } catch (error) {
      console.error('Error fetching bank data:', error);
    }
  };

  useEffect(() => {
    fetchBankData();
  }, [bank?.id]);

  const refreshBonusData = async () => {
    if (!user?.uid || !bank?.id) {
      console.log('Missing user or bank data for refresh');
      return;
    }

    try {
      const bankBonusRef = doc(db, 'users', user.uid, 'bankBonuses', bank.id);
      const userBonusRef = doc(db, 'userBonuses', `${user.uid}_${bank.id}`);
      const bankRef = doc(db, 'banks', bank.id);
      
      const [bankBonusDoc, userBonusDoc, bankDoc] = await Promise.all([
        getDoc(bankBonusRef),
        getDoc(userBonusRef),
        getDoc(bankRef)
      ]);

      let bonusData = null;

      // Prioritize userBonus data over bankBonus data
      if (userBonusDoc.exists()) {
        bonusData = userBonusDoc.data();
        console.log('Found userBonus data:', bonusData);
      } else if (bankBonusDoc.exists()) {
        bonusData = bankBonusDoc.data();
        console.log('Found bankBonus data:', bonusData);
      }

      if (bonusData) {
        // Ensure we have all required fields
        const validatedBonusData = {
          ...bonusData,
          status: bonusData.status || 'in_progress',
          startedAt: bonusData.startedAt || new Date(),
          completedAt: bonusData.completedAt || null,
          userId: user.uid,
          bankId: bank.id,
          userProgress: {
            ...(bonusData.userProgress || {}),
            status: bonusData.status || 'in_progress',
            startedAt: bonusData.startedAt || new Date(),
            completedAt: bonusData.completedAt || null,
          }
        };
        console.log('Setting validated bonus data:', validatedBonusData);
        setLocalExistingBonus(validatedBonusData);
        if (setExistingBonus) {
          setExistingBonus(validatedBonusData);
        }
      } else {
        console.log('No bonus data found, setting to null');
        setLocalExistingBonus(null);
        if (setExistingBonus) {
          setExistingBonus(null);
        }
      }

      // Update success rate from bank data
      if (bankDoc.exists()) {
        const bankData = bankDoc.data();
        // Set success rate to 0 if no attempts
        const newSuccessRate = bankData.totalAttempts > 0 
          ? Math.min(100, Math.round((bankData.successfulCompletions / bankData.totalAttempts) * 100))
          : 0;
        setLocalSuccessRate(newSuccessRate);
      }
    } catch (error) {
      console.error('Error refreshing bonus data:', error);
      enqueueSnackbar('Error refreshing bonus data: ' + error.message, { variant: 'error' });
    }
  };

  const handleBonusComplete = async (updatedBonus) => {
    try {
      if (!updatedBonus || !user?.uid || !bank?.id) {
        console.error('Missing required data for bonus completion:', { updatedBonus, userId: user?.uid, bankId: bank?.id });
        enqueueSnackbar('Missing required data for bonus completion', { variant: 'error' });
        return;
      }

      const bonusAmount = Number(updatedBonus.bonusAmount || bank.amount);
      const now = new Date();

      // Get current bank data and user bonus data
      const bankRef = doc(db, 'banks', bank.id);
      const userBonusRef = doc(db, 'userBonuses', `${user.uid}_${bank.id}`);
      const [bankDoc, userBonusDoc] = await Promise.all([
        getDoc(bankRef),
        getDoc(userBonusRef)
      ]);

      const bankData = bankDoc.exists() ? bankDoc.data() : {};
      const userBonusData = userBonusDoc.exists() ? userBonusDoc.data() : {};

      // Only increment metrics if the bonus wasn't already completed
      let newSuccessfulCompletions = bankData.successfulCompletions || 0;
      let newFailedCompletions = bankData.failedCompletions || 0;
      let newTotalAttempts = bankData.totalAttempts || 0;

      // Only update metrics if this is a new completion
      if (!userBonusData.completedAt) {
        newTotalAttempts += 1;
        if (updatedBonus.isPaidOut) {
          newSuccessfulCompletions += 1;
        } else {
          newFailedCompletions += 1;
        }
      }

      // Calculate success rate
      const successRate = newTotalAttempts > 0 
        ? Math.min(100, Math.round((newSuccessfulCompletions / newTotalAttempts) * 100))
        : 0;

      // Start a batch write
      const batch = writeBatch(db);

      // Update bank's success metrics using set with merge to ensure fields exist
      batch.set(bankRef, {
        successfulCompletions: newSuccessfulCompletions,
        failedCompletions: newFailedCompletions,
        totalAttempts: newTotalAttempts,
        successRate: successRate,
        lastUpdated: now
      }, { merge: true });

      // Update user bonus status
      const updatedBonusData = {
        status: 'completed',
        completedAt: now,
        progress: 100,
        progressPercentage: 100,
        isPaidOut: updatedBonus.isPaidOut,
        bonusAmount: bonusAmount,
        lastUpdated: now,
        userProgress: {
          status: 'completed',
          completedAt: now,
          progress: 100,
          progressPercentage: 100,
          lastUpdated: now
        }
      };
      batch.update(userBonusRef, updatedBonusData);

      // Commit all updates
      await batch.commit();

      // Update local states
      setLocalExistingBonus(updatedBonusData);
      if (setExistingBonus) {
        setExistingBonus(updatedBonusData);
      }
      setLocalSuccessRate(successRate);

      setCompleteBonusModalOpen(false);
      enqueueSnackbar('Bonus marked as completed successfully!', { variant: 'success' });

    } catch (error) {
      console.error('Error completing bonus:', error);
      enqueueSnackbar('Error completing bonus: ' + error.message, { variant: 'error' });
    }
  };

  const handleResetTracking = async () => {
    try {
      if (!user?.uid || !bank?.id) {
        console.error('Missing required data for reset');
        return;
      }

      const batch = writeBatch(db);
      const now = new Date();

      // Get references
      const bankRef = doc(db, 'banks', bank.id);
      const userBonusRef = doc(db, 'userBonuses', `${user.uid}_${bank.id}`);
      const bankBonusRef = doc(db, 'users', user.uid, 'bankBonuses', bank.id);

      // Get current bonus data to preserve in history
      const userBonusDoc = await getDoc(userBonusRef);
      const currentBonusData = userBonusDoc.exists() ? userBonusDoc.data() : null;

      // Save to history if there's completed bonus data to preserve
      if (currentBonusData && (currentBonusData.completedAt || currentBonusData.status === 'completed')) {
        const historyRef = doc(db, 'bonusHistory', `${user.uid}_${bank.id}_${now.getTime()}`);
        const historyData = {
          ...currentBonusData,
          userId: user.uid,
          bankId: bank.id,
          bankName: bank.name,
          resetAt: now,
          archivedAt: now,
          originalStartDate: currentBonusData.startedAt || currentBonusData.dateStarted,
          originalCompletionDate: currentBonusData.completedAt,
        };
        batch.set(historyRef, historyData);
        console.log('Saving bonus to history:', historyData);
      }

      // Get current bank data
      const bankDoc = await getDoc(bankRef);
      const bankData = bankDoc.exists() ? bankDoc.data() : {};

      console.log('=== RESET TRACKING DEBUG ===');
      console.log('Bank:', bank.name);
      console.log('Current bank data:', bankData);
      console.log('Local existing bonus:', localExistingBonus);

      // Initialize metrics with current values
      let newSuccessfulCompletions = bankData.successfulCompletions || 0;
      let newFailedCompletions = bankData.failedCompletions || 0;
      let newTotalAttempts = bankData.totalAttempts || 0;

      console.log('Initial metrics:', {
        successfulCompletions: newSuccessfulCompletions,
        failedCompletions: newFailedCompletions,
        totalAttempts: newTotalAttempts
      });

      // Only update metrics if the bonus was started
      if (localExistingBonus?.startedAt) {
        // If bonus was completed and paid out
        if (localExistingBonus.isPaidOut === true) {
          console.log('Decrementing successful completions and total attempts (paid out)');
          newSuccessfulCompletions = Math.max(0, newSuccessfulCompletions - 1);
          newTotalAttempts = Math.max(0, newTotalAttempts - 1);
        }
        // If bonus was started but not paid out
        else if (localExistingBonus.status === 'completed' || localExistingBonus.completedAt) {
          console.log('Decrementing failed completions and total attempts (not paid out)');
          newFailedCompletions = Math.max(0, newFailedCompletions - 1);
          newTotalAttempts = Math.max(0, newTotalAttempts - 1);
        }
      } else {
        console.log('Bonus was never started, not decrementing metrics');
      }

      // Calculate new success rate
      const successRate = newTotalAttempts > 0
        ? Math.min(100, Math.round((newSuccessfulCompletions / newTotalAttempts) * 100))
        : 0;

      console.log('Final calculated metrics:', {
        successfulCompletions: newSuccessfulCompletions,
        failedCompletions: newFailedCompletions,
        totalAttempts: newTotalAttempts,
        successRate: successRate
      });

      // Update bank metrics using set with merge to ensure fields exist
      const bankUpdateData = {
        successfulCompletions: newSuccessfulCompletions,
        failedCompletions: newFailedCompletions,
        totalAttempts: newTotalAttempts,
        successRate: successRate,
        lastUpdated: now
      };
      console.log('Updating bank with:', bankUpdateData);
      batch.set(bankRef, bankUpdateData, { merge: true });

      // Delete userBonus and bankBonus documents
      batch.delete(userBonusRef);
      batch.delete(bankBonusRef);

      // Commit all updates
      await batch.commit();
      console.log('Batch committed successfully!');
      console.log('=== END RESET TRACKING DEBUG ===');

      // Update local states
      setLocalExistingBonus(null);
      if (setExistingBonus) {
        setExistingBonus(null);
      }
      setLocalSuccessRate(successRate);

      setResetBonusModalOpen(false);
      enqueueSnackbar('Bonus tracking reset successfully. History preserved.', { variant: 'success' });

      // Refresh data to ensure everything is in sync
      await refreshBonusData();

    } catch (error) {
      console.error('Error resetting bonus tracking:', error);
      enqueueSnackbar('Error resetting bonus tracking: ' + error.message, { variant: 'error' });
    }
  };

  const updateLocalSuccessRate = async (bankId) => {
    try {
      const bankRef = doc(db, 'banks', bankId);
      const bankDoc = await getDoc(bankRef);
      if (bankDoc.exists()) {
        const bankData = bankDoc.data();
        // Ensure 0 if no attempts
        const newSuccessRate = bankData.totalAttempts > 0
          ? Math.min(100, Math.round((bankData.successfulCompletions / bankData.totalAttempts) * 100))
          : 0;
        setLocalSuccessRate(newSuccessRate);
      }
    } catch (error) {
      console.error('Error updating local success rate:', error);
    }
  };

  const handleBonusStatusChange = async (status, paidOut) => {
    try {
      const bankBonusRef = doc(db, 'users', user.uid, 'bankBonuses', bank.id);
      const userBonusRef = doc(db, 'userBonuses', `${user.uid}_${bank.id}`);
      const bankRef = doc(db, 'banks', bank.id);
      
      const now = new Date();

      // Get current bonus data to preserve startedAt
      const [userBonusDoc, bankBonusDoc, bankDoc] = await Promise.all([
        getDoc(userBonusRef),
        getDoc(bankBonusRef),
        getDoc(bankRef)
      ]);

      const userBonusData = userBonusDoc.exists() ? userBonusDoc.data() : {};
      const bankBonusData = bankBonusDoc.exists() ? bankBonusDoc.data() : {};
      const bankData = bankDoc.exists() ? bankDoc.data() : {};

      // Create the complete update data with proper completion timestamp and progress
      const updatedBonus = {
        ...localExistingBonus,
        status: status,
        completedAt: status === 'completed' ? now : null,
        progressPercentage: status === 'completed' ? 100 : 0,
        progress: status === 'completed' ? 100 : 0,
        lastUpdated: now,
        userId: user.uid,
        bankId: bank.id,
        bankName: bank.name,
        amount: bank.amount,
        startedAt: userBonusData.startedAt || bankBonusData.startedAt || localExistingBonus?.startedAt || now,
        updatedAt: now,
        paidOut: status === 'completed' ? Boolean(paidOut) : null,
        isPaidOut: status === 'completed' ? Boolean(paidOut) : null,
        userProgress: {
          progressPercentage: status === 'completed' ? 100 : 0,
          progress: status === 'completed' ? 100 : 0,
          startedAt: userBonusData.startedAt || bankBonusData.startedAt || localExistingBonus?.startedAt || now,
          completedAt: status === 'completed' ? now : null,
          status: status,
          lastUpdated: now
        }
      };

      // Calculate new metrics
      const currentSuccessful = bankData.successfulCompletions || 0;
      const currentFailed = bankData.failedCompletions || 0;
      const currentTotal = bankData.totalAttempts || 0;

      let newSuccessfulCompletions = currentSuccessful;
      let newFailedCompletions = currentFailed;
      let newTotalAttempts = currentTotal;

      // Only update metrics if status is changing to completed
      if (status === 'completed' && localExistingBonus?.status !== 'completed') {
        newTotalAttempts = currentTotal + 1;
        if (paidOut) {
          newSuccessfulCompletions = currentSuccessful + 1;
        } else {
          newFailedCompletions = currentFailed + 1;
        }
      }

      // Calculate new success rate
      const newSuccessRate = newTotalAttempts > 0
        ? Math.min(100, Math.round((newSuccessfulCompletions / newTotalAttempts) * 100))
        : 0;

      // Start Firestore updates
      const batch = writeBatch(db);

      // Update bank metrics
      if (status === 'completed' && localExistingBonus?.status !== 'completed') {
        batch.update(bankRef, {
          successfulCompletions: newSuccessfulCompletions,
          failedCompletions: newFailedCompletions,
          totalAttempts: newTotalAttempts,
          successRate: newSuccessRate,
          lastUpdated: now
        });
      }

      // Update both documents with complete data - use set with merge to preserve other fields
      batch.set(bankBonusRef, updatedBonus, { merge: true });
      batch.set(userBonusRef, updatedBonus, { merge: true });

      // Update local states immediately for instant UI feedback
      setLocalExistingBonus(updatedBonus);
      if (setExistingBonus) {
        setExistingBonus(updatedBonus);
      }
      setLocalSuccessRate(newSuccessRate);
      
      // Close modal immediately for better UX
      setCompleteBonusModalOpen(false);
      enqueueSnackbar('Bonus status updated successfully', { variant: 'success' });

      // Commit Firestore updates
      await batch.commit();

      // Refresh the data to ensure everything is in sync
      await refreshBonusData();
    } catch (error) {
      console.error('Error updating bonus status:', error);
      enqueueSnackbar('Failed to update bonus status: ' + error.message, { variant: 'error' });
    }
  };

  const handleRecalculateSuccessRate = async () => {
    if (!isAdmin || !bank?.id) {
      console.error('Unauthorized or missing bank ID');
      enqueueSnackbar('Unauthorized action', { variant: 'error' });
      return;
    }

    try {
      const bankRef = doc(db, 'banks', bank.id);
      const bankDoc = await getDoc(bankRef);

      if (bankDoc.exists()) {
        const bankData = bankDoc.data();
        const totalAttempts = Math.max(0, bankData.totalAttempts || 0);
        const successfulCompletions = Math.max(0, bankData.successfulCompletions || 0);
        const failedCompletions = Math.max(0, bankData.failedCompletions || 0);

        // Recalculate success rate from scratch
        const newSuccessRate = totalAttempts > 0
          ? Math.min(100, Math.round((successfulCompletions / totalAttempts) * 100))
          : 0;

        console.log('=== RECALCULATING SUCCESS RATE ===');
        console.log('Bank:', bank.name);
        console.log('Current data:', { totalAttempts, successfulCompletions, failedCompletions, successRate: bankData.successRate });
        console.log('New success rate:', newSuccessRate);

        await updateDoc(bankRef, {
          successRate: newSuccessRate,
          lastUpdated: new Date()
        });

        setLocalSuccessRate(newSuccessRate);
        enqueueSnackbar(`Success rate recalculated: ${newSuccessRate}%`, { variant: 'success' });

        // Refresh to show updated data
        await refreshBonusData();
      }
    } catch (error) {
      console.error('Error recalculating success rate:', error);
      enqueueSnackbar('Error recalculating success rate: ' + error.message, { variant: 'error' });
    }
  };

  const handleResetGlobalSuccessRate = async () => {
    if (!isAdmin || !bank?.id) {
      console.error('Unauthorized or missing bank ID');
      enqueueSnackbar('Unauthorized action', { variant: 'error' });
      return;
    }

    try {
      const bankRef = doc(db, 'banks', bank.id);
      const now = new Date();
      
      // Reset all success metrics to 0
      const resetData = {
        successfulCompletions: 0,
        failedCompletions: 0,
        totalAttempts: 0,
        successRate: 0,
        lastUpdated: now,
        // Reset legacy fields for backward compatibility
        numberComplete: 0,
        numberFailed: 0
      };

      // Use a batch to ensure atomic update
      const batch = writeBatch(db);
      batch.update(bankRef, resetData);
      await batch.commit();

      // Update local success rate immediately
      setLocalSuccessRate(0);
      
      enqueueSnackbar('Success rate reset successfully', { variant: 'success' });

      // Fetch fresh bank data to ensure everything is in sync
      await fetchBankData();
    } catch (error) {
      console.error('Error resetting success rate:', error);
      enqueueSnackbar('Failed to reset success rate: ' + error.message, { variant: 'error' });
    }
  };

  const handleBonusStarted = async (trackingData) => {
    try {
      // Update local states
      setLocalExistingBonus(trackingData);
      if (setExistingBonus) {
        setExistingBonus(trackingData);
      }

      // Close modal and show success message
      setStartBonusModalOpen(false);
      enqueueSnackbar('Started tracking bonus successfully', { variant: 'success' });

      // Refresh bonus data to ensure everything is in sync
      await refreshBonusData();
    } catch (error) {
      console.error('Error handling bonus start:', error);
      enqueueSnackbar('Error starting bonus tracking: ' + error.message, { variant: 'error' });
    }
  };

  useEffect(() => {
    if (user && bank?.id) {
      refreshBonusData();
    }
  }, [user, bank?.id]);

  useEffect(() => {
    setLocalSuccessRate(successRate);
  }, [successRate]);

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        width: '100%',
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: bgColors.main,
        p: 0,
        pt: 2,
        pb: 4,
        boxSizing: 'border-box',
        '& .MuiContainer-root': {
          maxWidth: '100% !important',
          width: '100%',
          p: 0,
          m: 0
        }
      }}
    >
      <Meta title={`${bank?.name || 'Bank'} Bonus Details`} />

      {/* Main Content Grid */}
      <Grid container spacing={0} sx={{
        width: '100%',
        m: 0,
        mt: 0,
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Left Column - Main Details */}
        <Grid item xs={12} md={9} sx={{
          p: 0,
          pt: 0,
          pl: { md: 2 },
          pr: { md: 2 },
          width: '100%',
          flexBasis: { xs: '100%', md: '75%' },
          maxWidth: { xs: '100%', md: '75%' }
        }}>
          <Stack spacing={2} sx={{ width: '100%' }}>
            {/* Bank Details Card */}
            <Card
              sx={{
                background: bgColors.card,
                borderRadius: 3,
                border: `1px solid ${bgColors.border}`,
                width: '100%',
                overflow: 'visible',
                p: 2.5,
                backdropFilter: 'blur(20px)',
              }}
            >
              <Stack spacing={1.5}>
                {/* Navigation Header with Bank Name */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {/* Left: Back Button and Bank Name */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <Button
                      onClick={handleBack}
                      startIcon={<ArrowBack />}
                      size="small"
                      sx={{
                        color: bgColors.text.secondary,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8125rem',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: bgColors.accent,
                          transform: 'translateX(-2px)',
                        },
                      }}
                    >
                      Back
                    </Button>

                    <Typography
                      variant="h4"
                      fontWeight="800"
                      sx={{
                        letterSpacing: '-0.02em',
                        color: bgColors.text.primary,
                      }}
                    >
                      {bank.name}
                    </Typography>

                    {bank.url && (
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={<OpenInNewIcon sx={{ fontSize: '1rem' }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(bank.url, '_blank', 'noopener,noreferrer');
                        }}
                        sx={{
                          fontSize: '0.8125rem',
                          padding: '6px 14px',
                          borderColor: alpha(bgColors.accent, 0.3),
                          color: bgColors.accent,
                          textTransform: 'none',
                          fontWeight: 600,
                          '&:hover': {
                            borderColor: bgColors.accent,
                            backgroundColor: alpha(bgColors.accent, 0.05),
                          }
                        }}
                      >
                        Bonus Link
                      </Button>
                    )}
                  </Box>

                  {/* Right: Navigation Arrows */}
                  <Stack direction="row" spacing={0.75}>
                    <Tooltip title="Previous Bank" arrow>
                      <span>
                        <IconButton
                          onClick={onPreviousBank}
                          disabled={!hasPrevious}
                          size="small"
                          sx={{
                            color: bgColors.text.secondary,
                            border: `1px solid ${bgColors.border}`,
                            borderRadius: 1.5,
                            width: '32px',
                            height: '32px',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              bgcolor: alpha(bgColors.accent, 0.1),
                              color: bgColors.accent,
                              borderColor: bgColors.accent,
                            },
                            '&:disabled': {
                              opacity: 0.3,
                              cursor: 'not-allowed',
                            }
                          }}
                        >
                          <ArrowBack sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Next Bank" arrow>
                      <span>
                        <IconButton
                          onClick={onNextBank}
                          disabled={!hasNext}
                          size="small"
                          sx={{
                            color: bgColors.text.secondary,
                            border: `1px solid ${bgColors.border}`,
                            borderRadius: 1.5,
                            width: '32px',
                            height: '32px',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              bgcolor: alpha(bgColors.accent, 0.1),
                              color: bgColors.accent,
                              borderColor: bgColors.accent,
                            },
                            '&:disabled': {
                              opacity: 0.3,
                              cursor: 'not-allowed',
                            }
                          }}
                        >
                          <ArrowForward sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>

                <Divider sx={{ borderColor: bgColors.border }} />

                {/* Chips and Amount */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                      <Chip
                        label={bank.status}
                        size="small"
                        sx={{
                          bgcolor: bank.status === 'verified'
                            ? alpha(bgColors.accent, darkMode ? 0.2 : 0.15)
                            : bank.status === 'rejected'
                            ? alpha('#EF4444', darkMode ? 0.2 : 0.15)
                            : alpha('#F59E0B', darkMode ? 0.2 : 0.15),
                          color: bank.status === 'verified'
                            ? bgColors.accent
                            : bank.status === 'rejected'
                            ? '#EF4444'
                            : '#F59E0B',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: '22px',
                          borderRadius: '6px',
                        }}
                      />
                      <Chip
                        label={bank.location}
                        size="small"
                        sx={{
                          bgcolor: alpha(bgColors.accent, darkMode ? 0.2 : 0.15),
                          color: bgColors.accent,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: '22px',
                          borderRadius: '6px',
                        }}
                      />
                      {bank.difficulty && (
                        <Chip
                          label={bank.difficulty}
                          size="small"
                          sx={{
                            bgcolor: alpha(bgColors.accent, darkMode ? 0.2 : 0.15),
                            color: bgColors.accent,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: '22px',
                            borderRadius: '6px',
                          }}
                        />
                      )}
                      {/* Inline Requirements */}
                      <Chip
                        label={`$${bank.directDeposit} DD`}
                        size="small"
                        sx={{
                          bgcolor: alpha(bgColors.primary, 0.1),
                          color: bgColors.primary,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: '22px',
                          borderRadius: '6px',
                        }}
                      />
                      <Chip
                        label={`${bank.timeframe} days`}
                        size="small"
                        sx={{
                          bgcolor: alpha(bgColors.primary, 0.1),
                          color: bgColors.primary,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: '22px',
                          borderRadius: '6px',
                        }}
                      />
                    </Stack>
                  </Stack>
                  <Typography
                    variant="h3"
                    fontWeight="800"
                    sx={{
                      background: darkMode
                        ? `linear-gradient(135deg, ${bgColors.primaryLight} 0%, ${bgColors.accent} 100%)`
                        : `linear-gradient(135deg, ${bgColors.primary} 0%, ${bgColors.accent} 100%)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    ${bank.amount}
                  </Typography>
                </Stack>

                {/* Quick Actions - Compact */}
                <Stack direction="row" spacing={1} className="bank-details-tracking" sx={{ width: '100%', alignItems: 'center' }}>
                  {localExistingBonus ? (
                    <>
                      {localExistingBonus.status !== 'completed' && (
                        <Button
                          variant="outlined"
                          color="warning"
                          onClick={() => setResetBonusModalOpen(true)}
                          size="small"
                          sx={{
                            px: 2,
                            py: 0.75,
                            height: '32px',
                            borderRadius: 1.5,
                            fontWeight: 600,
                            fontSize: '0.8125rem',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.warning.main, 0.08),
                            }
                          }}
                          startIcon={<RestartAltIcon sx={{ fontSize: '1rem' }} />}
                        >
                          Reset
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => setCompleteBonusModalOpen(true)}
                        disabled={localExistingBonus.status === 'completed'}
                        size="small"
                        sx={{
                          px: 2,
                          py: 0.75,
                          height: '32px',
                          borderRadius: 1.5,
                          fontWeight: 600,
                          fontSize: '0.8125rem',
                          opacity: localExistingBonus.status === 'completed' ? 0.6 : 1,
                        }}
                        startIcon={<CheckCircleIcon sx={{ fontSize: '1rem' }} />}
                      >
                        {localExistingBonus.status === 'completed' ? 'Completed' : 'Complete'}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={() => setStartBonusModalOpen(true)}
                      sx={{
                        bgcolor: bgColors.primary,
                        color: '#FFFFFF',
                        px: 2.5,
                        py: 0.75,
                        height: '32px',
                        borderRadius: 1.5,
                        fontWeight: 600,
                        fontSize: '0.8125rem',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: bgColors.primaryDark,
                          transform: 'translateY(-1px)',
                        }
                      }}
                    >
                      Start Tracking
                    </Button>
                  )}
                  <Box sx={{ flex: 1 }} />
                  <IconButton
                    onClick={handleBookmarkToggle}
                    size="small"
                    sx={{
                      color: isBookmarked ? bgColors.accent : bgColors.text.secondary,
                      bgcolor: isBookmarked ? alpha(bgColors.accent, 0.1) : 'transparent',
                      height: '32px',
                      width: '32px',
                      border: `1px solid ${isBookmarked ? alpha(bgColors.accent, 0.2) : bgColors.border}`,
                      borderRadius: 1.5,
                      '&:hover': {
                        bgcolor: alpha(bgColors.accent, 0.15),
                        color: bgColors.accent,
                      }
                    }}
                  >
                    {isBookmarked ? <BookmarkIcon sx={{ fontSize: '1.1rem' }} /> : <BookmarkBorderIcon sx={{ fontSize: '1.1rem' }} />}
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{
                      color: bgColors.text.secondary,
                      height: '32px',
                      width: '32px',
                      border: `1px solid ${bgColors.border}`,
                      borderRadius: 1.5,
                      '&:hover': {
                        bgcolor: alpha(bgColors.accent, 0.1),
                        color: bgColors.accent,
                      }
                    }}
                  >
                    <ShareIcon sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Stack>
              </Stack>
            </Card>

            {/* Requirements Details - Compact */}
            {bank.requirements && (
              <Card
                elevation={0}
                className="bank-details-requirements"
                sx={{
                  background: bgColors.card,
                  borderRadius: 3,
                  p: 2,
                  border: `1px solid ${bgColors.border}`,
                  backdropFilter: 'blur(20px)',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: bgColors.text.secondary,
                    lineHeight: 1.6,
                    whiteSpace: 'pre-line',
                    fontSize: '0.875rem',
                    '& ul': {
                      pl: 2,
                      listStyleType: 'none',
                      '& li': {
                        position: 'relative',
                        pl: 2.5,
                        mb: 0.75,
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: '0.5em',
                          width: 5,
                          height: 5,
                          backgroundColor: bgColors.accent,
                          borderRadius: '50%',
                        }
                      }
                    }
                  }}
                >
                  {bank.requirements}
                </Typography>
              </Card>
            )}

            {/* Discussion Section */}
            <Card
              elevation={0}
              className="bank-details-discussion"
              sx={{
                background: bgColors.card,
                borderRadius: 3,
                overflow: 'hidden',
                border: `1px solid ${bgColors.border}`,
                backdropFilter: 'blur(20px)',
                p: 2,
              }}
            >
              <BankDiscussion
                bankId={bank.id}
                isAdmin={isAdmin}
              />
            </Card>
          </Stack>
        </Grid>

        {/* Right Column - Stats & Actions */}
        <Grid item xs={12} md={3} sx={{
          p: 0,
          pt: 0,
          pr: { md: 2 },
          pl: { md: 0 },
          m: 0,
          width: '100%',
          flexBasis: { xs: '100%', md: '25%' },
          maxWidth: { xs: '100%', md: '25%' }
        }}>
          <Stack spacing={2} sx={{ width: '100%', position: 'sticky', top: 16, pr: 0, mr: 0 }}>
            <BankInsightsTile
              bank={bank}
              userBonus={localExistingBonus}
              totalUsers={totalUsers}
              successRate={localSuccessRate}
              onVote={handleVote}
              onReset={() => setResetBonusModalOpen(true)}
              isMobile={false}
            />

            {/* Admin Actions */}
            {isAdmin && (
              <Card
                elevation={0}
                sx={{
                  background: bgColors.card,
                  borderRadius: 3,
                  p: 2,
                  border: `1px solid ${bgColors.border}`,
                  backdropFilter: 'blur(20px)',
                }}
              >
                <Stack spacing={1.5}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: bgColors.text.primary,
                      fontWeight: 700,
                      letterSpacing: '-0.01em',
                      fontSize: '0.875rem',
                      mb: 0.5,
                    }}
                  >
                    Admin Actions
                  </Typography>
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={() => handleStatusChange('verified')}
                    disabled={bank.status === 'verified'}
                    sx={{
                      bgcolor: bank.status === 'verified'
                        ? alpha(bgColors.accent, 0.5)
                        : bgColors.primary,
                      color: '#FFFFFF',
                      borderRadius: 1.5,
                      fontWeight: 600,
                      py: 0.75,
                      fontSize: '0.8125rem',
                      '&:hover': {
                        bgcolor: bgColors.primaryDark,
                      }
                    }}
                    startIcon={<CheckCircleIcon sx={{ fontSize: '1rem' }} />}
                  >
                    Verify
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={handleRecalculateSuccessRate}
                    sx={{
                      borderColor: alpha(theme.palette.warning.main, 0.5),
                      color: theme.palette.warning.main,
                      borderRadius: 1.5,
                      fontWeight: 600,
                      py: 0.75,
                      fontSize: '0.8125rem',
                      '&:hover': {
                        borderColor: theme.palette.warning.main,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                      }
                    }}
                    startIcon={<TrendingUpIcon sx={{ fontSize: '1rem' }} />}
                  >
                    Recalc Rate
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={handleResetGlobalSuccessRate}
                    sx={{
                      borderColor: alpha(theme.palette.error.main, 0.5),
                      color: theme.palette.error.main,
                      borderRadius: 1.5,
                      fontWeight: 600,
                      py: 0.75,
                      fontSize: '0.8125rem',
                      '&:hover': {
                        borderColor: theme.palette.error.main,
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                      }
                    }}
                    startIcon={<RestartAltIcon sx={{ fontSize: '1rem' }} />}
                  >
                    Reset Rate
                  </Button>
                </Stack>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* Modals */}
      <StartBonusModal
        open={startBonusModalOpen}
        onClose={() => setStartBonusModalOpen(false)}
        bank={bank}
        onSuccess={handleBonusStarted}
      />
      <CompleteBonusModal
        open={completeBonusModalOpen}
        onClose={() => setCompleteBonusModalOpen(false)}
        bank={bank}
        existingBonus={localExistingBonus}
        onComplete={handleBonusComplete}
      />
      <ResetBonusModal
        open={resetBonusModalOpen}
        onClose={() => setResetBonusModalOpen(false)}
        bank={bank}
        existingBonus={localExistingBonus}
        onReset={handleResetTracking}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        sx={{ bottom: 24 }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WebBankDetails; 